import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

const bootLines = [
    "INITIALISING TAK_OS v2.6.0......................[OK]",
    "LOADING NEURAL CORE MODULES....................[OK]",
    "CALIBRATING MARKET INTELLIGENCE................[OK]",
    "MOUNTING FOUNDER CONSCIOUSNESS.................[OK]",
    "ALL SYSTEMS OPERATIONAL. WELCOME."
];

const P1 = `Let's be honest.
Every business in this world right now is either:
Confused about AI | Scared of AI | Pretending they already "do AI"

We've watched brilliant founders and businesses sit on the sidelines of the biggest 
technological shift of our generation not because they lacked ambition, but because AI felt too 
technical, too foreign, or too expensive to access. " That ends here "

The AI Kompany was built on one belief: AI shouldn't be a privilege for Silicon Valley startups 
with $10M in funding. It should be working for the retail shop owner tracking inventory, the D2C brand 
automating customer support, and the agency founder who's tired of manually writing 47 variations of 
the same caption, same old static cold emails. That is the gap The AI Kompany was built to close. We 
believe AI should work like electricity. Invisible, reliable, always on. You stop thinking about it and 
start thinking about growing your business instead.`;
const P2 = `Two engineers. One obsession. We left comfortable corporate careers in software architecture 
and management because we believed there was something more interesting to build than what we were being asked 
to build inside them. We are not a two-hundred-person consultancy hiding behind a polished pitch deck. We are 
builders who use every tool on our own business before we ever recommend it to yours. We are not here to sell 
you transformation. We are here to make next Tuesday better than last Tuesday. That is how real change actually 
happens.`;

export default function About() {
    const [bootDone, setBootDone] = useState(false);
    const [currentLine, setCurrentLine] = useState(0);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    useEffect(() => {
        if (currentLine < bootLines.length) {
            const t = setTimeout(() => {
                setCurrentLine((prev) => prev + 1);
            }, 350);
            return () => clearTimeout(t);
        } else {
            const t = setTimeout(() => setBootDone(true), 1200);
            return () => clearTimeout(t);
        }
    }, [currentLine]);

    useEffect(() => {
        const skip = () => setBootDone(true);
        window.addEventListener("click", skip);
        window.addEventListener("keydown", skip);
        return () => {
            window.removeEventListener("click", skip);
            window.removeEventListener("keydown", skip);
        };
    }, []);

    return (
        <div className="bg-[#0B0F14] text-[#3DF2E0] min-h-screen font-mono relative">

            {/* SCANLINES */}
            <div className="pointer-events-none fixed inset-0 z-50 bg-[repeating-linear-gradient(0deg,transparent,transparent_2px,rgba(0,0,0,0.2)_2px,rgba(0,0,0,0.2)_4px)]" />

            {!bootDone ? (
                <BootScreen currentLine={currentLine} />
            ) : (
                <Terminal />
            )}
        </div>
    );
}

function Label({ text }) {
    return (
        <div className="text-xs text-[#1FBFB0] tracking-widest border-l-2 border-[#163D3A] pl-3 mt-6">
            {text}
        </div>
    );
}

function Command({ text }) {
    return (
        <div className="flex gap-2 items-start">
            <span className="text-[#3DF2E0] text-sm">{">_"}</span>
            <span className="text-[#1FBFB0] text-sm">{text}</span>
        </div>
    );
}

function BootScreen({ currentLine }) {
    return (
        <div className="flex flex-col justify-center min-h-screen px-10">

            {bootLines.map((line, i) => (
                <div
                    key={i}
                    className={`text-sm text-[#3DF2E0] transition-opacity duration-300 ${i < currentLine ? "opacity-100" : "opacity-0"
                        }`}
                >
                    {line}
                </div>
            ))}

            {currentLine >= bootLines.length && (
                <>
                    <div className="mt-6 text-5xl font-bold tracking-widest text-[#3DF2E0]">
                        THE AI KOMPANY
                    </div>

                    <div className="text-xs text-[#1FBFB0] mt-2 tracking-widest">
            // BENGALURU, INDIA | EST. 2026 | ABOUT_US.EXE
                    </div>

                    <div className="mt-6 text-xs animate-pulse text-[#163D3A]">
                        [ PRESS ANY KEY OR WAIT TO CONTINUE ]
                    </div>
                </>
            )}
        </div>
    );
}

function Terminal() {
    const navigate = useNavigate();
    return (
        <div className="p-6">
            <div className="border border-[#163D3A]">

                {/* HEADER */}
                <div className="flex items-center px-4 h-9 border-b border-[#163D3A] bg-[#0E2A28] relative">

                    {/* LEFT DOTS */}
                    <div className="flex gap-2">
                        <div className="w-3 h-3 bg-red-400 rounded-full" />
                        <div className="w-3 h-3 bg-yellow-400 rounded-full" />
                        <div className="w-3 h-3 bg-green-400 rounded-full" />
                    </div>

                    {/* CENTER TITLE*/}
                    <div className="absolute left-1/2 transform -translate-x-1/2 text-xs text-[#1FBFB0]">
                        TAK_TERMINAL — about_us.exe
                    </div>

                    {/* RIGHT EXIT BUTTON*/}
                    <div className="ml-auto">
                        <button
                            onClick={() => navigate(-1)}
                            className="text-xs text-[#1FBFB0] hover:text-[#3DF2E0] transition"
                        >
                            |exit_terminal|
                        </button>
                    </div>

                </div>

                {/* CONTENT */}
                <div className="p-10 max-w-3xl mx-auto space-y-6">

                    <Command text="TAK origin_mission.txt" />
                    <Label text="// ORIGIN & MISSION" />
                    <Typewriter text={P1} />


                    <Command text="TAK who_we_are.txt" />
                    <Label text="// WHO WE ARE" />
                    <Typewriter text={P2} />

                    {/*<ImageReveal label="// TEAM_BENGALURU_HQ" />*/}

                    <FounderSection />

                </div>
            </div>
        </div>
    );
}

function Typewriter({ text }) {
    const [display, setDisplay] = useState("");

    useEffect(() => {
        let i = 0;
        const interval = setInterval(() => {
            setDisplay(text.slice(0, i + 1));
            i++;
            if (i >= text.length) clearInterval(interval);
        }, 18);

        return () => clearInterval(interval);
    }, [text]);

    return (
        <p className="leading-relaxed text-[#3DF2E0]">
            {display}
            <span className="inline-block w-2 h-4 bg-[#3DF2E0] ml-1 animate-pulse" />
        </p>
    );
}

function ImageReveal({ label }) {
    return (
        <div className="border border-[#163D3A] max-w-md relative overflow-hidden mt-6">
            <img
                src="https://images.unsplash.com/photo-1677442136019-21780ecad995"
                className="w-full grayscale brightness-75 sepia hue-rotate-[160deg]"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-transparent to-[#0B0F14]" />
            <div className="absolute bottom-2 left-3 text-xs text-[#1FBFB0]">
                {label}
            </div>
        </div>
    );
}

function FounderSection() {
    return (
        <div className="mt-16 border-t border-[#163D3A] pt-8 space-y-16">

            {/* TITLE */}
            <div className="text-xs text-[#1FBFB0] tracking-[3px]">
        // MEET OUR FOUNDERS
            </div>

            {/* FOUNDER 1 */}
            <div className="flex flex-col md:flex-row gap-6 md:gap-8 items-start">

                {/* IMAGE */}
                <div className="w-full md:w-40 border border-[#163D3A] bg-[#0E2A28] overflow-hidden flex-shrink-0 mx-auto md:mx-0">
                    <img
                        src="/images/nidish.jpeg"
                        alt="Nidish"
                        className="w-full h-auto object-cover"
                    />
                </div>

                {/* TEXT */}
                <div className="space-y-3 max-w-full md:max-w-xl text-center md:text-left">

                    <h2 className="text-2xl md:text-3xl text-[#3DF2E0] leading-tight break-words">
                        NIDISH ANAND R.
                    </h2>

                    <p className="text-xs md:text-sm text-[#1FBFB0] tracking-widest">
      // FOUNDER & CEO
                    </p>

                    <p className="text-sm text-[#2ACFC1] leading-relaxed">
                        Former software architect. Recovered corporate employee.
                    </p>

                </div>

            </div>

            {/* FOUNDER 2 */}

            <div className="flex flex-col md:flex-row gap-6 md:gap-8 items-start">

                {/* IMAGE */}
                <div className="w-full md:w-40 border border-[#163D3A] bg-[#0E2A28] overflow-hidden flex-shrink-0 mx-auto md:mx-0">
                    <img
                        src="/images/srini.jpeg"
                        alt="Srinivas"
                        className="w-full h-auto object-cover"
                    />
                </div>

                {/* TEXT */}
                <div className="space-y-3 max-w-full md:max-w-xl text-center md:text-left">

                    <h2 className="text-2xl md:text-3xl text-[#3DF2E0] leading-tight break-words">
                        SRINIVAS S.
                    </h2>

                    <p className="text-xs md:text-sm text-[#1FBFB0] tracking-widest">
      // CO-FOUNDER & COO
                    </p>

                    <p className="text-sm text-[#2ACFC1] leading-relaxed">
                        Previously managed teams that moved slow.
                        Left to build one that moves fast.
                    </p>

                </div>

            </div>

        </div>
    );
}